import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { PackageSearch, RefreshCw, WifiOff } from "lucide-react";
import { publicApi } from "@/lib/api";
import { GENERAL_ENQUIRY } from "@/lib/store";
import { useCatalogSync } from "@/hooks/useCatalogSync";
import { CategoryTabs, SearchBar } from "./CatalogueControls";
import { ProductCard } from "./ProductCard";
import { ProductModal } from "./ProductModal";

const useDebounced = (value, ms) => {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
};

const Skeletons = () => Array.from({ length: 6 }).map((_, i) => (
  <div key={i} className="h-[380px] animate-pulse rounded-2xl border border-[#E5E0D8] bg-white/70" />
));

const Empty = ({ onReset }) => (
  <div className="col-span-full flex flex-col items-center rounded-2xl border border-dashed border-[#C59B27]/50 bg-white/60 px-6 py-14 text-center" data-testid="catalogue-empty-state">
    <PackageSearch className="h-10 w-10 text-[#8B5A2B]" />
    <h3 className="mt-4 font-display text-2xl text-[#1B4332]">Nothing matched — but we may still have it</h3>
    <p className="mt-2 max-w-md text-sm text-[#5B625D]">Many items in the shop aren't listed online yet. Send us a message and we'll check for you.</p>
    <div className="mt-6 flex flex-col gap-2 sm:flex-row">
      <a href={GENERAL_ENQUIRY} target="_blank" rel="noopener noreferrer" className="inline-flex h-11 items-center rounded-full bg-[#25D366] px-6 font-semibold text-[#0B3B1F]" data-testid="empty-whatsapp-btn">Ask on WhatsApp</a>
      <button onClick={onReset} className="h-11 rounded-full border border-[#1B4332]/30 px-6 font-semibold text-[#1B4332]" data-testid="empty-reset-btn">Clear filters</button>
    </div>
  </div>
);

export const Catalogue = () => {
  const [params, setParams] = useSearchParams();
  const [category, setCategory] = useState("All Products");
  const [search, setSearch] = useState("");
  const [availability, setAvailability] = useState("");
  const q = useDebounced(search, 300);
  const { online } = useCatalogSync();

  const cats = useQuery({ queryKey: ["categories"], queryFn: publicApi.categories });
  const list = useInfiniteQuery({
    queryKey: ["products", category, q, availability],
    queryFn: ({ pageParam }) => publicApi.products({ category, q: q || undefined, availability: availability || undefined, page: pageParam, page_size: 12 }),
    initialPageParam: 1,
    getNextPageParam: (last) => (last.page < last.pages ? last.page + 1 : undefined),
  });
  const items = list.data?.pages.flatMap((p) => p.items) || [];
  const total = list.data?.pages[0]?.total ?? 0;

  const slug = params.get("product");
  const detail = useQuery({ queryKey: ["product", slug], queryFn: () => publicApi.product(slug), enabled: !!slug, retry: false });
  const openProduct = (p) => setParams((s) => { s.set("product", p.slug); return s; }, { replace: false });
  const closeProduct = () => setParams((s) => { s.delete("product"); return s; }, { replace: true });
  const reset = () => { setSearch(""); setAvailability(""); setCategory("All Products"); };

  return (
    <section id="catalogue" className="section-pad scroll-mt-20" data-testid="catalogue-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8B5A2B]">Our shelves</p>
        <div className="mt-2 flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#1B4332]">Browse the catalogue</h2>
          <p className="text-sm text-[#5B625D]" data-testid="catalogue-result-count">{list.isSuccess ? `${total} item${total === 1 ? "" : "s"} shown` : "\u00a0"}</p>
        </div>
        <div className="mt-8 space-y-4">
          <CategoryTabs categories={cats.data?.items} total={cats.data?.total} active={category} onChange={setCategory} />
          <SearchBar value={search} onChange={setSearch} availability={availability} onAvailability={setAvailability} />
        </div>
        {!online && (
          <p className="mt-4 flex items-center gap-2 rounded-xl bg-[#FBF1D6] px-4 py-2 text-sm text-[#7A5A0B]" data-testid="offline-notice"><WifiOff className="h-4 w-4" /> You're offline — showing the last loaded products.</p>
        )}
        <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="product-grid">
          {list.isPending && <Skeletons />}
          {list.isError && !items.length && (
            <div className="col-span-full rounded-2xl border border-[#E5E0D8] bg-white p-8 text-center" data-testid="catalogue-error">
              <p className="text-[#3E4540]">We couldn't load products right now.</p>
              <button onClick={() => list.refetch()} className="mt-4 inline-flex h-11 items-center gap-2 rounded-full bg-[#1B4332] px-6 font-semibold text-white" data-testid="catalogue-retry-btn"><RefreshCw className="h-4 w-4" /> Try again</button>
            </div>
          )}
          {list.isSuccess && !items.length && <Empty onReset={reset} />}
          {items.map((p) => <ProductCard key={p.id} product={p} onOpen={openProduct} />)}
        </div>
        {list.hasNextPage && (
          <div className="mt-10 text-center">
            <button onClick={() => list.fetchNextPage()} disabled={list.isFetchingNextPage} className="h-12 rounded-full border-2 border-[#1B4332] px-8 font-semibold text-[#1B4332] hover:bg-[#1B4332] hover:text-white disabled:opacity-60" data-testid="load-more-btn">
              {list.isFetchingNextPage ? "Loading…" : "Show more products"}
            </button>
          </div>
        )}
      </div>
      <ProductModal product={detail.data} open={!!slug && !!detail.data} onOpenChange={(o) => { if (!o) closeProduct(); }} />
    </section>
  );
};
